import React, { createContext, useContext, useState, ReactNode } from "react";
import { Button } from "../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  DialogDescription,
} from "../design/dialog";
import { Input } from "../design/input";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { useAttorney } from "../contexts/AttorneyContext";
import PhoneNumberInput from "../components/PhoneNumberInput";
import { Text } from "../design/text";

interface Errors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  firmName?: string;
  saveError?: string;
}

interface AttorneyDialogContextType {
  openCreateDialog: (options?: { onAttorneyCreated?: (attorneyId: string) => void }) => void;
}

const AttorneyDialogContext = createContext<
  AttorneyDialogContextType | undefined
>(undefined);

export const useAttorneyDialog = () => {
  const context = useContext(AttorneyDialogContext);
  if (!context) {
    throw new Error(
      "useAttorneyDialog must be used within an AttorneyDialogProvider",
    );
  }
  return context;
};

interface Props {
  children: ReactNode;
}

export const AttorneyDialogProvider: React.FC<Props> = ({ children }) => {
  const { createAttorney } = useAttorney();

  const [isOpen, setIsOpen] = useState(false);
  const [onAttorneyCreatedCallback, setOnAttorneyCreatedCallback] = useState<
    ((attorneyId: string) => void) | null
  >(null);

  // Form state
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [suffix, setSuffix] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [firmName, setFirmName] = useState<string>("");
  const [barNumber, setBarNumber] = useState<string>("");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  const openCreateDialog = (options?: {
    onAttorneyCreated?: (attorneyId: string) => void;
  }) => {
    clearForm();
    // Store callback as a function that returns the callback to avoid React treating it as a state updater
    setOnAttorneyCreatedCallback(() => options?.onAttorneyCreated || null);
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const cleanPhoneNumber = phoneNumber
        ? `+${phoneNumber.replace(/\D/g, "")}`
        : undefined;
      const attorneyId = await createAttorney({
        firstName,
        lastName,
        suffix: suffix || undefined,
        phoneNumber: cleanPhoneNumber,
        email: email || undefined,
        firmName: firmName || undefined,
        barNumber: barNumber || undefined,
        status: "active",
      });
      if (onAttorneyCreatedCallback && attorneyId) {
        onAttorneyCreatedCallback(attorneyId);
      }
      handleClose();
    } catch (error) {
      console.error("Error creating attorney:", error);
      setErrors({ saveError: (error as Error).message });
    } finally {
      setIsSaving(false);
    }
  };

  const validate = () => {
    const newErrors: Errors = {};
    if (!firstName) newErrors.firstName = "First Name is required";
    if (!lastName) newErrors.lastName = "Last Name is required";
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      newErrors.email = "Invalid email address";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearForm = () => {
    setFirstName("");
    setLastName("");
    setSuffix("");
    setEmail("");
    setPhoneNumber("");
    setFirmName("");
    setBarNumber("");
    setErrors({});
  };

  const handleClose = () => {
    setIsOpen(false);
    clearForm();
  };

  return (
    <AttorneyDialogContext.Provider value={{ openCreateDialog }}>
      {children}
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Add Attorney</DialogTitle>
        <DialogDescription>
          Add a new attorney to your account. They can then be assigned to cases.
        </DialogDescription>
        <DialogBody className="space-y-6">
          {errors.saveError && (
            <div className="p-3 border border-red-200 rounded-lg bg-red-50 dark:border-red-800 dark:bg-red-900/20">
              <Text className="text-sm text-red-700 dark:text-red-300">
                {errors.saveError}
              </Text>
            </div>
          )}
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 sm:gap-4">
                <Field className="sm:col-span-1">
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={isSaving}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field className="sm:col-span-1">
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={isSaving}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
                <Field className="sm:col-span-1">
                  <Label>Suffix</Label>
                  <Input
                    type="text"
                    value={suffix}
                    placeholder="Esq."
                    onChange={(e) => setSuffix(e.target.value)}
                    disabled={isSaving}
                  />
                </Field>
              </div>
              <Field>
                <Label>Firm Name</Label>
                <Input
                  type="text"
                  value={firmName}
                  placeholder="Law Firm Name"
                  onChange={(e) => setFirmName(e.target.value)}
                  disabled={isSaving}
                />
              </Field>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    placeholder="attorney@example.com"
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSaving}
                  />
                  {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                </Field>
                <Field>
                  <Label>Phone Number</Label>
                  <PhoneNumberInput
                    defaultValue={phoneNumber}
                    value={phoneNumber}
                    placeholder="Phone Number"
                    onChange={setPhoneNumber}
                  />
                </Field>
              </div>
              <Field>
                <Label>Bar Number</Label>
                <Input
                  type="text"
                  value={barNumber}
                  placeholder="State Bar Number"
                  onChange={(e) => setBarNumber(e.target.value)}
                  disabled={isSaving}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSubmit}>
            {isSaving ? "Saving..." : "Save Attorney"}
          </Button>
        </DialogActions>
      </Dialog>
    </AttorneyDialogContext.Provider>
  );
};
